import { useState, useEffect, useRef } from 'react';
import { getJobLogs } from '../api';
import '../styles/components/progress.css';

function classifyMsg(msg) {
  if (!msg) return '';
  if (msg.startsWith('Result: CONFIRMED') || msg.startsWith('Result: FOUND') || msg.startsWith('DONE'))
    return 'msg-success';
  if (msg.startsWith('Result: PROBABLE'))
    return 'msg-muted';
  if (msg.startsWith('Result: AMBIGUOUS'))
    return 'msg-warn';
  if (msg.startsWith('Result: NOT_FOUND'))
    return 'msg-neutral';
  if (msg.startsWith('FAILED') || msg.startsWith('Result: ERROR'))
    return 'msg-error';
  if (msg === 'Processing...' || msg === 'Processing started')
    return 'msg-start';
  return '';
}

function isRowEnd(msg) {
  if (!msg) return false;
  return msg.startsWith('DONE') || msg.startsWith('FAILED') || msg.startsWith('Result: ');
}

const LOG_TAIL = 5000;

export default function LiveLog({ jobId, active, onCurrentRow }) {
  const [logs, setLogs] = useState([]);
  const sinceIdRef = useRef(0);
  const bodyRef = useRef();
  const autoScrollRef = useRef(true);
  const [loaded, setLoaded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const onCurrentRowRef = useRef(onCurrentRow);
  onCurrentRowRef.current = onCurrentRow;

  useEffect(() => {
    let cancelled = false;
    const fetchLatest = async () => {
      try {
        const opts = sinceIdRef.current
          ? { sinceId: sinceIdRef.current }
          : { tail: LOG_TAIL };
        const data = await getJobLogs(jobId, opts);
        if (cancelled) return;
        if (data.logs && data.logs.length > 0) {
          if (sinceIdRef.current) {
            setLogs(prev => [...prev, ...data.logs]);
          } else {
            setLogs(data.logs);
            if (data.logs.length >= LOG_TAIL) setTruncated(true);
          }
          sinceIdRef.current = data.logs[data.logs.length - 1].id;
          if (onCurrentRowRef.current) {
            const last = [...data.logs].reverse().find(l => l.row_index != null);
            if (last) onCurrentRowRef.current({ row_index: last.row_index, name: last.name });
          }
        }
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchLatest();
    if (active) {
      const interval = setInterval(fetchLatest, 2000);
      return () => { cancelled = true; clearInterval(interval); };
    }
    return () => { cancelled = true; };
  }, [jobId, active]);

  // Auto-scroll
  useEffect(() => {
    if (autoScrollRef.current && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [logs]);

  const handleScroll = () => {
    if (!bodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  };

  if (loaded && logs.length === 0 && !active) return null;

  return (
    <div className="live-log">
      <div className="live-log-header">
        <span>{active ? 'Live Activity' : 'Activity Log'}</span>
        {active && <div className="live-dot" />}
      </div>
      <div className="live-log-body" ref={bodyRef} onScroll={handleScroll}>
        {truncated && (
          <div className="log-row" style={{ color: 'var(--text3)', justifyContent: 'center', fontStyle: 'italic' }}>
            Showing last {LOG_TAIL.toLocaleString()} log entries
          </div>
        )}
        {logs.map((log, i) => {
          const msgClass = classifyMsg(log.message);
          const rowEnd = isRowEnd(log.message);
          return (
            <div key={log.id} className={`log-row${rowEnd ? ' log-row-end' : ''}${msgClass === 'msg-start' ? ' log-row-start' : ''}`}>
              <span className="log-idx">
                {log.row_index != null ? `#${log.row_index}` : ''}
              </span>
              <span className="log-name">{log.name || ''}</span>
              <span className={`log-msg ${msgClass || (log.level ? `msg-${log.level}` : '')}`}>{log.message}</span>
              <span className={`log-level ${log.level}`}>{log.level}</span>
            </div>
          );
        })}
        {logs.length === 0 && active && (
          <div className="log-row" style={{ color: 'var(--text3)', justifyContent: 'center' }}>
            Waiting for activity...
          </div>
        )}
      </div>
    </div>
  );
}
